<script lang="ts" setup>
import {
  Groups,
  LoadingSpaceDimensions,
  TechDataVehicle,
  VehicleDimensions,
  Weights,
  Body,
  Engine,
  EmissionsConsumption,
} from '@/types';
import { tableTitles } from '../utils/tableLabels';
import TableValuesItem from '@/components/table-values-item.vue';
import { getDefaultLocaleValue } from '../locales/default';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import TechDataModalHeader from './tech-data-modal-header.vue';
import { useTracking } from '../composables/useTracking';
import { vHasScroll } from '../directives/v-has-scroll';

type TransmissionMode = 'manual' | 'automatic';

const props = defineProps<{
  translations: Record<string, string>;
  vehicle: TechDataVehicle;
  vehicles: TechDataVehicle[];
  isOpen: boolean;
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  transmissionMode: TransmissionMode | null;
}>();
const mode = computed<TransmissionMode | null>(() => {
  const m = props.transmissionMode;
  if (!m) return null;

  return m;
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const animatingIn = ref(false);
let lastOpenedByEvent: EventTarget | null = null;
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

// Tracking
const { trackModalScroll } = useTracking(props.modelSeries);

const tableWrapperRef = ref<HTMLElement | null>(null);
let hasTrackedScrollEnd = false;

watch(
  () => props.isOpen,
  async (open: boolean) => {
    const dialog = dialogRef.value;
    if (!dialog) return;

    if (open) {
      animatingIn.value = false;
      lastOpenedByEvent = document.activeElement;
      document.body.style.overflow = 'hidden';

      dialog.showModal();

      await nextTick();

      // Reset scroll position when opening the modal.
      // Without this reset the modal would reopen at the previous scroll position.
      dialog.scrollTop = 0;

      requestAnimationFrame(() => {
        animatingIn.value = true;

        dialog.removeEventListener('scroll', throttledScrollHandler);
        dialog.addEventListener('scroll', throttledScrollHandler, {
          passive: true,
        });
      });

      hasTrackedScrollEnd = false;
    } else {
      animatingIn.value = false;
      document.body.style.overflow = '';

      dialog.removeEventListener('scroll', throttledScrollHandler);

      await waitForDialogTransitionEnd(dialog);

      if (dialog.open) dialog.close();
    }
  },
);

function waitForDialogTransitionEnd(dialog: HTMLDialogElement) {
  return new Promise<void>((resolve) => {
    const el = dialog;
    const fallback = window.setTimeout(() => resolve(), 300);

    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) return;
      if (e.propertyName !== 'opacity' && e.propertyName !== 'transform')
        return;

      el.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      resolve();
    };

    el.addEventListener('transitionend', onEnd);
  });
}

function handleOutsideClick(event: MouseEvent) {
  const dialog = dialogRef.value;
  if (!dialog) return;

  if (event.target === lastOpenedByEvent) {
    lastOpenedByEvent = null;
    return;
  }

  const rect = dialog.getBoundingClientRect();
  const clickInside =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom;

  if (!clickInside && props.isOpen) {
    emit('close');
  }
}

function throttle<F extends (...args: any[]) => any>(func: F, delay: number) {
  let lastCall = 0;

  return (...args: Parameters<F>): ReturnType<F> | undefined => {
    const now = Date.now();
    if (now - lastCall < delay) return undefined;

    lastCall = now;
    return func(...args);
  };
}

function handleScroll() {
  const el = dialogRef.value;
  if (!el || hasTrackedScrollEnd) return;

  const scrollTop = el.scrollTop;
  const clientHeight = el.clientHeight;
  const scrollHeight = el.scrollHeight;
  const threshold = Math.max(10, Math.round(clientHeight * 0.05));

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    trackModalScroll('view_full_modal', selectedVehicle.value ?? props.vehicle);
    hasTrackedScrollEnd = true;
  }
}

const throttledScrollHandler = throttle(handleScroll, 50);

onMounted(() => {
  window.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  window.removeEventListener('click', handleOutsideClick);

  // Clear scroll timeout if it exists
  if (scrollTimeout) clearTimeout(scrollTimeout);
});

// Selected vehicle (default)
const initialSelectedVehicle = props.vehicle ?? props.vehicles[0] ?? null;
const selectedVehicle = ref(initialSelectedVehicle);

// Rows are based on the selectedVehicle
const computedTables = computed(() =>
  tableTitles
    .map((table) => selectedVehicle.value?.[table as keyof Groups])
    .filter((table) => table && !areAllKeysNull(table)),
);

const tableLabels = computed(() =>
  Object.keys(selectedVehicle.value ?? {})
    .filter(
      (key) =>
        !areAllKeysNull((selectedVehicle.value ?? {})[key as keyof Groups]),
    )
    .filter((key) => tableTitles.includes(key)),
);

function areAllKeysNull(obj: any) {
  for (let key in obj) {
    if (key !== 'label' && obj.hasOwnProperty(key) && obj[key] !== null) {
      return false;
    }
  }
  return true;
}

function getDataPoints(
  table?:
    | VehicleDimensions
    | LoadingSpaceDimensions
    | Weights
    | Body
    | Engine
    | EmissionsConsumption,
  mode?: 'manual' | 'automatic' | null,
) {
  if (!table) return [];

  const skipKeys = new Set(['label', 'engineName']);

  const record = table as Record<string, unknown>;

  const manualBlock = record['manual'] as Record<string, unknown> | undefined;
  const automaticBlock = record['automatic'] as
    | Record<string, unknown>
    | undefined;

  const getNonNullKeys = (obj?: Record<string, unknown>) =>
    obj ? Object.keys(obj).filter((k) => obj[k] != null) : [];

  const hasManual = getNonNullKeys(manualBlock).length > 0;
  const hasAutomatic = getNonNullKeys(automaticBlock).length > 0;

  const chosenMode: 'manual' | 'automatic' | null =
    mode ?? (hasManual ? 'manual' : hasAutomatic ? 'automatic' : null);

  return Object.entries(record).flatMap(([key, raw]) => {
    if (skipKeys.has(key) || raw == null) return [];

    if (key === 'manual') {
      if (chosenMode !== 'manual') return [];
      return getNonNullKeys(raw as Record<string, unknown>);
    }

    if (key === 'automatic') {
      if (chosenMode !== 'automatic') return [];
      return getNonNullKeys(raw as Record<string, unknown>);
    }

    return [key];
  });
}

function getEngineName(v: any) {
  return v?.engine?.engineName ?? v?.vmdsTypeId ?? `${v.trim ?? ''}`;
}

async function handleCancel() {
  if (!props.isOpen) return;

  emit('close');
}

watch(
  () => props.vehicle,
  (v) => {
    selectedVehicle.value = v ?? props.vehicles[0] ?? null;
  },
  { immediate: true },
);

watch(
  () => props.vehicles,
  (list) => {
    if (!selectedVehicle.value && list?.length) {
      selectedVehicle.value = list[0];
    }
  },
  { immediate: true },
);
</script>

<template>
  <dialog
    ref="dialogRef"
    role="dialog"
    aria-modal="true"
    aria-labelledby="tech-data-modal-title"
    class="owvans-tech-data-dialog"
    :class="{ '-animate-in': animatingIn }"
    @cancel.prevent="handleCancel"
  >
    <TechDataModalHeader
      :translations="translations"
      :vehicle="selectedVehicle"
      :validOptions="validOptions"
      :transmissionMode="mode"
      :close="
        () => {
          emit('close');
        }
      "
    />

    <div class="owvans-tech-data-dialog__table-wrapper" ref="tableWrapperRef">
      <div class="owvans-tech-data-dialog__table-scroll" v-has-scroll>
        <table class="wbx-table owvans-tech-data-dialog__table">
          <colgroup>
            <col class="owvans-tech-data-dialog__col-sticky" />
            <col v-for="(_, i) in vehicles" :key="i" />
          </colgroup>
          <thead>
            <tr>
              <th class="owvans-tech-data-dialog__sticky-cell" scope="col"></th>
              <th
                v-for="(vehicle, index) in vehicles"
                :key="index"
                scope="col"
                class="owvans-tech-data-dialog__labels wb-text-l-strong"
              >
                {{ getEngineName(vehicle) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(table, tableIndex) in computedTables"
              :key="tableIndex"
            >
              <tr>
                <th
                  class="owvans-tech-data-dialog__section-title"
                  scope="row"
                  :id="`tech-data-modal-title-${tableIndex}`"
                >
                  <div>
                    {{
                      translations[
                        `owvc_tech_data_${tableLabels[tableIndex]}`
                      ] || getDefaultLocaleValue(tableLabels[tableIndex])
                    }}
                  </div>
                </th>

                <th
                  class="owvans-tech-data-dialog__section-title owvans-tech-data-dialog__section-fill"
                  :colspan="vehicles.length"
                  aria-hidden="true"
                ></th>
              </tr>

              <TableValuesItem
                v-for="dataKey in getDataPoints(table)"
                :key="dataKey"
                :vehicles="vehicles"
                :locale="locale"
                :parentLabel="tableLabels[tableIndex]"
                :label="dataKey"
                :translations="translations"
                :transmissionMode="mode"
              />
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </dialog>
</template>

<style lang="scss" scoped>
.owvans-tech-data-dialog {
  --_duration: 300ms;
  --dialog-max-width: min(86%, 105rem);
  --dialog-max-height: 1000px;

  position: fixed;
  inset: 0;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  padding: 0;

  background-color: var(--wb-data-table-background-main-color);
  border: none;

  opacity: 0;
  transform: translateY(50%);
  transition:
    transform var(--_duration) var(--wb-move),
    opacity var(--_duration) var(--wb-fade);

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(2px);
    transition: opacity 0.3s ease;
  }

  &.-animate-in {
    opacity: 1;
    transform: translateY(0);
    &::backdrop {
      opacity: 1;
    }
  }

  @media (min-width: 1024px) {
    max-width: var(--dialog-max-width);
    max-height: var(--dialog-max-height);
  }

  :deep(.owvans-tech-data-dialog__col-sticky) {
    width: min(40vw, 210px);
  }

  &__table-wrapper {
    margin-left: 32px;

    overflow-y: auto;
  }

  &__table-scroll {
    width: 100%;

    margin-bottom: 24px;

    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: none;
  }

  &__table {
    table-layout: auto;

    width: calc(100% - 2rem);

    margin-right: 32px;
  }

  :deep(.owvans-tech-data-dialog__table th) {
    padding-top: 16px;
    padding-bottom: 16px;

    @media (min-width: 1024px) {
      padding-top: 24px;
      padding-bottom: 24px;
    }
  }

  &__sticky-cell {
    position: sticky;
    left: 0;
    z-index: 2;

    max-width: 140px;
  }

  .v-has-scroll-x {
    :deep(.owvans-tech-data-dialog__sticky-cell::after) {
      content: '';
      position: absolute;
      inset: -5px 3px -5px 0;

      box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
      opacity: 0.8;
    }
  }

  &__table thead &__sticky-cell {
    background: #fff;
  }

  :deep(
    .owvans-tech-data-dialog__table
      tbody
      tr:has(+ tr > th.owvans-tech-data-dialog__section-title)
      > th
  ) {
    padding-bottom: 32px;
  }

  &__section-title {
    position: sticky;
    left: 0;
    z-index: 4;

    max-width: 140px;

    background: var(--table-background-striped-color);
    font-weight: 700;
  }

  .v-has-scroll-x {
    .owvans-tech-data-dialog__table
      .owvans-tech-data-dialog__section-title::after {
      content: none;
    }
  }

  &__section-title > div {
    white-space: nowrap;
  }

  &__section-fill {
    z-index: 0;
    padding: 0;
    border-left: none;
  }

  th {
    text-align: right;
  }

  tr:has(+ tr th.owvans-tech-data-dialog__section-title) {
    border-bottom: none;
  }

  .owvans-tech-data-dialog__section-title,
  &__sticky-cell {
    text-align: left;
  }
}
</style>
